import { MANUFACTURER_SOURCE_KINDS, type Product, type SourceKind } from './schema';

/**
 * Provenance gate. Runs after zod validation; returns human-readable problems
 * (empty = pass). These are the rules AUTHORING.md states — keep them in sync.
 */
export function auditProvenance(p: Product): string[] {
  const problems: string[] = [];
  const used = new Set<string>();

  const kindsOf = (src: string[]): SourceKind[] =>
    src.map((k) => p.sources[k]?.kind).filter((k): k is SourceKind => k !== undefined);

  const check = (path: string, src: string[], critical: boolean) => {
    for (const k of src) {
      used.add(k);
      if (!p.sources[k]) problems.push(`${path}: source "${k}" is not defined in sources`);
    }
    if (critical && !kindsOf(src).some((k) => MANUFACTURER_SOURCE_KINDS.includes(k))) {
      problems.push(`${path}: needs at least one manufacturer source (manual, spec_sheet, drawing, product_page)`);
    }
  };

  // Dimensions drive the 3D model and every placement check: two independent
  // sources, or one manufacturer document that states them authoritatively.
  check('body', p.body.src, true);
  const bodyKinds = kindsOf(p.body.src);
  const authoritative = bodyKinds.some((k) => k === 'manual' || k === 'spec_sheet' || k === 'drawing');
  if (new Set(p.body.src).size < 2 && !authoritative) {
    problems.push('body: needs two sources, or one manual / spec_sheet / drawing');
  }

  check('msrpUsd', p.msrpUsd.src, false);
  if (p.weightKg) check('weightKg', p.weightKg.src, false);

  const s = p.specs;
  switch (s.kind) {
    case 'speaker':
      check('specs.active', s.active.src, true);
      if (s.impedanceOhm) check('specs.impedanceOhm', s.impedanceOhm.src, true);
      if (s.sensitivityDb) check('specs.sensitivityDb', s.sensitivityDb.src, true);
      if (s.lowHz) check('specs.lowHz', s.lowHz.src, true);
      if (s.recAmpW) check('specs.recAmpW', s.recAmpW.src, true);
      check('specs.drivers', s.drivers.src, true);
      check('specs.port', s.port.src, false);
      break;
    case 'subwoofer':
      check('specs.ampW', s.ampW.src, true);
      check('specs.drivers', s.drivers.src, true);
      check('specs.port', s.port.src, false);
      check('specs.lowHz', s.lowHz.src, true);
      check('specs.inputs', s.inputs.src, true);
      break;
    case 'av':
      check('specs.ampChannels', s.ampChannels.src, true);
      check('specs.processingChannels', s.processingChannels.src, true);
      check('specs.heightDecoding', s.heightDecoding.src, true);
      check('specs.preouts', s.preouts.src, true);
      if (s.power) check('specs.power', s.power.src, true);
      if (s.minImpedanceOhm) check('specs.minImpedanceOhm', s.minImpedanceOhm.src, true);
      if (s.impedanceSettings) check('specs.impedanceSettings', s.impedanceSettings.src, true);
      if (s.supportedLayouts) check('specs.supportedLayouts', s.supportedLayouts.src, true);
      if (s.roomCorrection) check('specs.roomCorrection', s.roomCorrection.src, false);
      break;
    case 'power_amp':
      check('specs.channels', s.channels.src, true);
      check('specs.power', s.power.src, true);
      check('specs.minImpedanceOhm', s.minImpedanceOhm.src, true);
      check('specs.inputs', s.inputs.src, true);
      break;
    case 'tv':
      check('specs.screenIn', s.screenIn.src, true);
      check('specs.resolution', s.resolution.src, true);
      check('specs.refreshHz', s.refreshHz.src, true);
      check('specs.hdmiInputs', s.hdmiInputs.src, true);
      check('specs.earc', s.earc.src, true);
      break;
    case 'projector':
      check('specs.lumensAnsi', s.lumensAnsi.src, true);
      check('specs.resolution', s.resolution.src, true);
      check('specs.throwRatio', s.throwRatio.src, true);
      check('specs.hdmiInputs', s.hdmiInputs.src, true);
      break;
    case 'projector_screen':
      check('specs.diagonalIn', s.diagonalIn.src, true);
      check('specs.aspect', s.aspect.src, true);
      check('specs.gain', s.gain.src, true);
      check('specs.format', s.format.src, true);
      check('specs.acousticallyTransparent', s.acousticallyTransparent.src, true);
      break;
    default: {
      // Every spec kind must be audited: a new kind fails to compile here.
      const unaudited: never = s;
      void unaudited;
    }
  }

  for (const k of Object.keys(p.sources)) {
    if (!used.has(k)) problems.push(`sources.${k}: defined but never cited`);
  }
  return problems;
}
